import React from "react";
import { Button, Message } from "semantic-ui-react";
import {
  ListBuilder,
  Table,
  ActionBar,
  Format,
  ActionMenu,
  useApiAction,
} from "@redriver/cinnamon";
import { TablePagination } from "components/pagination";
import { PermissionCheck } from "components/auth";
import { Actions, Targets } from "constants/permissions";
import {
  getUsers,
  addUsers,
  updateUserStatus,
  getSsoSummary,
  getMfaSummary,
} from "./actions";
import FilterUsers from "./FilterUsers";
import { MaplesLabel, StatusPill } from "components/containers";
import { Avatar, SortHeader } from "components/misc";
import { getInitials } from "modules/helpers";
import { Sort } from "constants/enums";
import { AddUsers } from "features/Admin/AddUsers";
import { UserActions } from "features/Admin/UserActions";
import { UserSortFields } from "constants/sortFields";
import BulkActionsModal from "components/modals/BulkActionsModal";
import {
  sendPasswordReset,
  updateUserMfa,
  resetUserMfa,
} from "features/Admin/UserActions/actions";
import { useHistory, useLocation } from "react-router-dom";
import { UserRoutes } from "constants/routes";
import { UserStatus, UserAuthType, UserAuthTypeLabels } from "constants/enums";
import DownloadUsers from "./DownloadUsers";

const AUTH_TYPE_LABEL_TYPES = {
  [UserAuthType.SsoEnabled]: "primary",
  [UserAuthType.PasswordOnly]: "secondary",
  [UserAuthType.MfaActive]: "active",
  [UserAuthType.MfaEnforced]: "inactive",
  [UserAuthType.NotApplicable]: "default",
};

const ListUsers = ({ organisationId = null, organisationName }) => {
  const history = useHistory();
  const location = useLocation();
  const [{ response: ssoSummary }] = useApiAction(getSsoSummary, {
    lazy: false,
  });
  const [{ response: mfaSummary }] = useApiAction(getMfaSummary, {
    lazy: false,
  });
  return (
    <ListBuilder
      withTable
      loadAction={getUsers}
      onLoaded={() => {
        if (organisationId || organisationName) {
          // clean up the displayed url
          history.replace(location.pathname);
        }
      }}
      initialFilters={{
        sortName: "name",
        sortDirection: Sort.Asc,
        organisationId: organisationId,
        organisationName: organisationName,
        status: [UserStatus.Active],
      }}
      dataTransform={(data) => data.results}
      totalTransform={(data) => data.totalResults}
      initialPagination={{ pageSize: 25, pageNumber: 1 }}
      renderList={(tableProps, state, events) => {
        const totalSelected = state.selection.allSelected
          ? state.total - (state.selection.deselected?.length || 0)
          : state.selection.selected?.length || 0;

        const visibleRowCount = tableProps?.data?.length;

        // the rows we can actually verify applicability against - either the full visible page (when "all" is
        // selected, since rows on other pages can't be checked) or just the explicitly checked rows
        const getSelectionRows = () => {
          const rows = tableProps?.data || [];
          if (state.selection.allSelected) return rows;
          const selectedIds = state.selection.selected || [];
          return rows.filter((u) => selectedIds.includes(u.id));
        };

        // only show a bulk action if it applies to every row we can verify, so a mixed selection - or an
        // unverifiable "select all" - never surfaces an action that wouldn't make sense for some of the selection
        const isBulkActionApplicable = (predicate) => {
          const rows = getSelectionRows();
          return rows.length > 0 && rows.every((u) => predicate(u));
        };
        const canBulkActivate = isBulkActionApplicable((u) => u.isDisabled);
        const canBulkLockOut = isBulkActionApplicable((u) => !u.isDisabled);

        // enforcing/disabling MFA can override an organisation's own setting for a user,
        // which only makes sense to reason about (and warn about) when every selected
        // user belongs to the same organisation - a mixed-organisation selection can't
        // be described by one warning, so those actions are hidden for it entirely
        const mfaEligibleRows = getSelectionRows().filter(
          (u) => !u.signsInWithMicrosoft
        );
        const sharedOrganisationId =
          mfaEligibleRows.length > 0
            ? mfaEligibleRows[0].organisationId || null
            : null;
        const sharesSingleOrganisation =
          mfaEligibleRows.length > 0 &&
          mfaEligibleRows.every(
            (u) => (u.organisationId || null) === sharedOrganisationId
          );
        const sharedOrganisationEnforceMfa =
          mfaEligibleRows[0]?.organisationEnforceMfa || false;

        const bulkMfaWarning = (targetEnforceMfa) =>
          sharedOrganisationId &&
          sharedOrganisationEnforceMfa !== targetEnforceMfa ? (
            <Message
              warning
              style={{ marginTop: "1rem" }}
              header="Please Note"
              content={`The selected users' organisation currently ${
                sharedOrganisationEnforceMfa ? "enforces" : "does not enforce"
              } MFA for its users. If you go ahead, the organisational MFA settings will be overridden for these users, are you sure you wish to continue?`}
            />
          ) : undefined;

        const canBulkEnableMfa =
          isBulkActionApplicable(
            (u) => !u.signsInWithMicrosoft && !u.enforceMfa
          ) && sharesSingleOrganisation;
        const canBulkDisableMfa =
          isBulkActionApplicable(
            (u) => !u.signsInWithMicrosoft && u.enforceMfa
          ) && sharesSingleOrganisation;
        const canBulkResetMfa = isBulkActionApplicable(
          (u) => !u.signsInWithMicrosoft && u.mfaEnrolled
        );
        const canBulkSendPasswordReset = isBulkActionApplicable(
          (u) => !u.signsInWithMicrosoft && !u.isDisabled && u.isVerified
        );
        return (
          <div>
            {(ssoSummary || mfaSummary) && (
              <div className="status-pills">
                {ssoSummary && (
                  <StatusPill type="primary">
                    {ssoSummary.ssoEnabledCount} of{" "}
                    {ssoSummary.internalUserCount} internal users on SSO
                  </StatusPill>
                )}
                {mfaSummary && (
                  <StatusPill type="secondary">
                    {mfaSummary.mfaEnrolledCount} of{" "}
                    {mfaSummary.eligibleUserCount} eligible users on MFA
                  </StatusPill>
                )}
              </div>
            )}
            <ActionBar>
              <ActionBar.Item>
                <FilterUsers
                  value={state.filters}
                  onChange={events.onChangeFilters}
                />
              </ActionBar.Item>
              <ActionBar.Item fluid align="right">
                <ActionMenu
                  openOnHover={false}
                  disabled={totalSelected == 0}
                  trigger={<Button compact> Actions ({totalSelected}) </Button>}
                >
                  {canBulkActivate && (
                    <BulkActionsModal
                      actionName="activate"
                      header="Activate"
                      submitParams={{
                        isDisabled: false,
                        selected: state.selection.selected,
                        deselected: state.selection.deselected,
                        allSelected: state.selection.allSelected,
                        search: state.filters.search,
                      }}
                      onRefresh={events.onRefresh}
                      submitAction={updateUserStatus}
                      totalSelected={totalSelected}
                      as={ActionMenu.Item}
                      entityName="user"
                    >
                      Activate
                    </BulkActionsModal>
                  )}
                  {canBulkLockOut && (
                    <BulkActionsModal
                      actionName="lock out"
                      header="Lock Out"
                      submitParams={{
                        isDisabled: true,
                        selected: state.selection.selected,
                        deselected: state.selection.deselected,
                        allSelected: state.selection.allSelected,
                        search: state.filters.search,
                      }}
                      onRefresh={events.onRefresh}
                      submitAction={updateUserStatus}
                      totalSelected={totalSelected}
                      as={ActionMenu.Item}
                      entityName="user"
                    >
                      Lock Out
                    </BulkActionsModal>
                  )}
                  {canBulkSendPasswordReset && (
                    <BulkActionsModal
                      actionName="send password reset email to"
                      header="Send password Reset"
                      submitParams={{
                        selected: state.selection.selected,
                        deselected: state.selection.deselected,
                        allSelected: state.selection.allSelected,
                        search: state.filters.search,
                      }}
                      submitAction={sendPasswordReset}
                      totalSelected={totalSelected}
                      as={ActionMenu.Item}
                      entityName="user"
                    >
                      Send Password Reset
                    </BulkActionsModal>
                  )}
                  {canBulkEnableMfa && (
                    <BulkActionsModal
                      actionName="enforce multi-factor authentication for"
                      header="Enforce MFA"
                      warning={bulkMfaWarning(true)}
                      submitParams={{
                        enforceMfa: true,
                        selected: state.selection.selected,
                        deselected: state.selection.deselected,
                        allSelected: state.selection.allSelected,
                        search: state.filters.search,
                      }}
                      onRefresh={events.onRefresh}
                      submitAction={updateUserMfa}
                      totalSelected={totalSelected}
                      as={ActionMenu.Item}
                      entityName="user"
                    >
                      Enforce MFA
                    </BulkActionsModal>
                  )}
                  {canBulkDisableMfa && (
                    <BulkActionsModal
                      actionName="disable multi-factor authentication for"
                      header="Disable MFA Enforcement"
                      warning={bulkMfaWarning(false)}
                      submitParams={{
                        enforceMfa: false,
                        selected: state.selection.selected,
                        deselected: state.selection.deselected,
                        allSelected: state.selection.allSelected,
                        search: state.filters.search,
                      }}
                      onRefresh={events.onRefresh}
                      submitAction={updateUserMfa}
                      totalSelected={totalSelected}
                      as={ActionMenu.Item}
                      entityName="user"
                    >
                      Disable MFA Enforcement
                    </BulkActionsModal>
                  )}
                  {canBulkResetMfa && (
                    <BulkActionsModal
                      actionName="reset multi-factor authentication for"
                      header="Reset Multi-Factor Authentication"
                      submitParams={{
                        selected: state.selection.selected,
                        deselected: state.selection.deselected,
                        allSelected: state.selection.allSelected,
                        search: state.filters.search,
                      }}
                      onRefresh={events.onRefresh}
                      submitAction={resetUserMfa}
                      totalSelected={totalSelected}
                      as={ActionMenu.Item}
                      entityName="user"
                    >
                      Reset MFA
                    </BulkActionsModal>
                  )}
                </ActionMenu>
                <PermissionCheck action={Actions.Create} target={Targets.User}>
                  <AddUsers
                    compact
                    content="Add Users"
                    onAdded={events.onRefresh}
                    submitAction={addUsers}
                    title="Add Users"
                    allowExistingUsers={false}
                  />
                </PermissionCheck>
                <PermissionCheck action={Actions.View} target={Targets.User}>
                  <DownloadUsers
                    selection={state.selection}
                    filters={state.filters}
                    compact
                    disabled={state.total == 0}
                  >
                    Download{" "}
                    {totalSelected == 0 || totalSelected == state.total
                      ? "All"
                      : "Selected"}{" "}
                    ({totalSelected == 0 ? state.total : totalSelected})
                  </DownloadUsers>
                </PermissionCheck>
              </ActionBar.Item>
            </ActionBar>
            <Table
              {...tableProps}
              dataKey="id"
              emptyMessage="No users found"
              selectable
              className="users-table"
              onRowClick={(user) => history.push(UserRoutes.User(user.id))}
            >
              <Table.SelectableColumn
                value={state.selection}
                onChange={events.onChangeSelection}
                totalItems={state.total}
              />
              <Table.Column
                width="5%"
                render={(user) => {
                  const initials = getInitials([user.firstName, user.lastName]);
                  return <Avatar initials={initials} />;
                }}
              />
              <Table.Column
                header={
                  <SortHeader
                    title="User"
                    field={UserSortFields.Name}
                    value={state.filters}
                    onChange={events.onChangeFilters}
                  />
                }
                render={(user) => (
                  <div className="stacked-column">
                    <span className="name">
                      {user.firstName + " " + user.lastName}
                    </span>
                    <span>{user.email}</span>
                  </div>
                )}
                width="22%"
              />

              <Table.Column
                field="role"
                width="11%"
                header="Type"
                render={(user) =>
                  user.isSystemAdmin ? (
                    <MaplesLabel type="primary">System Admin</MaplesLabel>
                  ) : (
                    <MaplesLabel type="default">Standard User</MaplesLabel>
                  )
                }
              />

              <Table.Column
                header={
                  <SortHeader
                    title="Organisation"
                    field={UserSortFields.Organisation}
                    value={state.filters}
                    onChange={events.onChangeFilters}
                  />
                }
                field="organisationName"
                render={(user) => user.organisationName || "-"}
              />

              <Table.Column
                header={
                  <SortHeader
                    title="Status"
                    field={UserSortFields.Status}
                    value={state.filters}
                    onChange={events.onChangeFilters}
                  />
                }
                width="10%"
                render={(user) =>
                  user.isDisabled ? (
                    <MaplesLabel type="default">Inactive</MaplesLabel>
                  ) : user.isVerified ? (
                    <MaplesLabel type="primary">Active</MaplesLabel>
                  ) : (
                    <MaplesLabel type="secondary">Unverified</MaplesLabel>
                  )
                }
              />
              <Table.Column
                header="Auth Type"
                width="12%"
                render={(user) => (
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: "0.35rem",
                    }}
                  >
                    {(user.authType || []).map((type) => (
                      <MaplesLabel
                        key={type}
                        type={AUTH_TYPE_LABEL_TYPES[type]}
                      >
                        {UserAuthTypeLabels[type]}
                      </MaplesLabel>
                    ))}
                  </div>
                )}
              />

              <Table.Column
                header={
                  <SortHeader
                    title="Groups"
                    field={UserSortFields.Groups}
                    value={state.filters}
                    onChange={events.onChangeFilters}
                  />
                }
                field="groupsCount"
              />

              <Table.Column
                header={
                  <SortHeader
                    title="Invited"
                    field={UserSortFields.Created}
                    value={state.filters}
                    onChange={events.onChangeFilters}
                  />
                }
                width="13%"
                render={(user) => (
                  <Format.DateTime
                    value={user.createdUtc}
                    format="DD MMM YYYY HH:mm"
                  />
                )}
              />

              <Table.Column
                header={
                  <SortHeader
                    title="Last Login"
                    field={UserSortFields.Login}
                    value={state.filters}
                    onChange={events.onChangeFilters}
                  />
                }
                width="13%"
                render={(user) =>
                  user.lastLoginUtc ? (
                    <Format.DateTime
                      value={user.lastLoginUtc}
                      format="DD MMM YYYY HH:mm"
                    />
                  ) : (
                    <MaplesLabel type="primary">Not Logged In</MaplesLabel>
                  )
                }
              />

              <Table.Column
                width="5%"
                align="right"
                render={(user, colState) => (
                  <UserActions
                    user={user}
                    onRefresh={events.onRefresh}
                    itemIndex={colState.itemIndex}
                    visibleRowCount={visibleRowCount}
                  />
                )}
              />
            </Table>
            <TablePagination
              value={state.pagination}
              onChange={events.onChangePagination}
              totalItems={state.total}
              pageSizeOptions={[10, 25, 50, 100]}
            />
          </div>
        );
      }}
    />
  );
};

export default ListUsers;
